// Tableau de bord IA — vue d'ensemble de tous les outils d'intelligence artificielle
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  AlertTriangle,
  Users,
  Zap,
  BarChart2,
  RefreshCw,
  CheckCircle,
  Clock,
  Brain,
  Activity,
  Database,
} from "lucide-react";
import React from "react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useAdminPageTitle } from "@/hooks/admin/useAdminPageTitle";
import { KPICard } from "@/components/admin/shared/KPICard";
import { EmptyState } from "@/components/admin/shared/EmptyState";
import { useAIDashboardKPIs, useSalesInsights, useModelStatus } from "@/hooks/admin/useAI";
import type { InsightCategory, SalesInsight, ModelStatusEntry } from "@/lib/admin-api/ai";

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

function formatFcfa(n: number): string {
  return `${n.toLocaleString("fr-SN")} FCFA`;
}

function formatDate(d: string | null): string {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-SN");
}

// ---------------------------------------------------------------------------
// Insight category helpers
// ---------------------------------------------------------------------------

const INSIGHT_CATEGORY_LABELS: Record<InsightCategory, string> = {
  opportunity: "Opportunité",
  risk: "Risque",
  trend: "Tendance",
};

const INSIGHT_CATEGORY_COLORS: Record<InsightCategory, string> = {
  opportunity: "bg-blue-100 text-blue-700",
  risk: "bg-red-100 text-red-700",
  trend: "bg-purple-100 text-purple-700",
};

const INSIGHT_CATEGORY_ICONS: Record<InsightCategory, React.ElementType> = {
  opportunity: TrendingUp,
  risk: AlertTriangle,
  trend: BarChart2,
};

// ---------------------------------------------------------------------------
// Sub-tool cards
// ---------------------------------------------------------------------------

interface ToolCard {
  readonly title: string;
  readonly description: string;
  readonly href: string;
  readonly Icon: React.ElementType;
  readonly color: string;
}

const TOOL_CARDS: readonly ToolCard[] = [
  {
    title: "Prévisions demande",
    description: "Prévisions de vente sur 7, 30 et 90 jours par produit",
    href: "/admin/ai/forecast",
    Icon: TrendingUp,
    color: "text-blue-600 bg-blue-50",
  },
  {
    title: "Tarification intelligente",
    description: "Suggestions de prix optimisés basées sur la demande",
    href: "/admin/ai/smart-pricing",
    Icon: Zap,
    color: "text-amber-600 bg-amber-50",
  },
  {
    title: "Risque désabonnement",
    description: "Clients à risque de départ identifiés par l'IA",
    href: "/admin/ai/churn",
    Icon: Users,
    color: "text-red-600 bg-red-50",
  },
  {
    title: "Anomalies",
    description: "Détection automatique d'anomalies dans les données",
    href: "/admin/ai/anomalies",
    Icon: AlertTriangle,
    color: "text-purple-600 bg-purple-50",
  },
];

// ---------------------------------------------------------------------------
// Insight card
// ---------------------------------------------------------------------------

function InsightCard({ insight }: { readonly insight: SalesInsight }) {
  const Icon = INSIGHT_CATEGORY_ICONS[insight.category] ?? BarChart2;
  const categoryColor = INSIGHT_CATEGORY_COLORS[insight.category] ?? "bg-stone-100 text-stone-600";
  const categoryLabel = INSIGHT_CATEGORY_LABELS[insight.category] ?? insight.category;
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-start gap-3">
        <span className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${categoryColor}`}>
          <Icon className="h-3 w-3" />
          {categoryLabel}
        </span>
        <span className="ml-auto text-xs text-stone-400">{insight.confidence_percent}% confiance</span>
      </div>
      <p className="text-sm font-semibold text-stone-900">{insight.title}</p>
      <p className="mt-1 text-sm text-stone-500">{insight.description}</p>
      {insight.impact_fcfa != null && (
        <p className="mt-2 text-xs font-medium text-stone-700">
          Impact estimé : {formatFcfa(insight.impact_fcfa)}
        </p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Model card (real data from registry)
// ---------------------------------------------------------------------------

function ModelCard({ model }: { readonly model: ModelStatusEntry }) {
  const isActive = model.status === "active";
  return (
    <div className="rounded-lg border border-stone-200 bg-white p-4">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-stone-400" />
          <span className="text-sm font-medium text-stone-900">{model.display_name}</span>
        </div>
        <span
          className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${
            isActive ? "bg-green-100 text-green-700" : "bg-stone-100 text-stone-500"
          }`}
        >
          {isActive ? <CheckCircle className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
          {isActive ? "Actif" : "En attente"}
        </span>
      </div>
      <p className="text-xs text-stone-500">{model.description}</p>
      {model.backtested_accuracy_percent != null ? (
        <p className="mt-2 text-xs text-stone-500">
          Précision testée :{" "}
          <span className="font-medium text-stone-700">{model.backtested_accuracy_percent}%</span>
          {model.accuracy_metric && (
            <span className="ml-1 text-stone-400">({model.accuracy_metric.toUpperCase()})</span>
          )}
        </p>
      ) : (
        <p className="mt-2 text-xs text-stone-400">Précision : en attente de données</p>
      )}
      <p className="mt-1 text-xs text-stone-400">
        {model.last_computed_at ? `Dernier calcul : ${formatDate(model.last_computed_at)}` : "Pas encore calculé"}
        {model.data_points_used > 0 && ` · ${model.data_points_used} points`}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

export default function AIDashboard() {
  useAdminPageTitle("Intelligence artificielle");
  const navigate = useNavigate();
  const { data: kpis, isLoading: kpisLoading } = useAIDashboardKPIs();
  const { data: insightsData, isLoading: insightsLoading } = useSalesInsights();
  const { data: modelsData, isLoading: modelsLoading } = useModelStatus();

  const insights = insightsData?.insights?.slice(0, 5) ?? [];
  const models = modelsData?.models ?? [];

  return (
    <AdminLayout>
      <div className="space-y-8 p-6">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-semibold text-stone-900">Intelligence Artificielle</h1>
          <p className="mt-1 text-sm text-stone-500">
            Vue d'ensemble des outils IA — prévisions, tarification, désabonnement, anomalies
          </p>
        </div>

        {/* Status banner */}
        <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <strong>Algorithmes statistiques déployés</strong> — Les métriques sont calculées à partir
          des données réelles de vente. Les précisions affichées sont testées sur données historiques.
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
          <KPICard
            label="Score qualité données"
            value={kpisLoading ? "…" : `${kpis?.data_quality_score ?? 0}%`}
            icon={<Database className="h-4 w-4" />}
          />
          <KPICard
            label="Gain tarification estimé"
            value={kpisLoading ? "…" : formatFcfa(kpis?.pricing_revenue_gain_fcfa ?? 0)}
            icon={<Zap className="h-4 w-4" />}
          />
          <KPICard
            label="Clients haut risque"
            value={kpisLoading ? "…" : String(kpis?.churn_high_risk_count ?? 0)}
            icon={<Users className="h-4 w-4" />}
          />
          <KPICard
            label="Anomalies détectées"
            value={kpisLoading ? "…" : String(kpis?.anomalies_detected ?? 0)}
            icon={<AlertTriangle className="h-4 w-4" />}
          />
          <KPICard
            label="Commandes / jour"
            value={kpisLoading ? "…" : String(kpis?.avg_orders_per_day ?? 0)}
            icon={<Activity className="h-4 w-4" />}
          />
          <KPICard
            label="Dernière mise à jour"
            value={kpisLoading ? "…" : formatDate(kpis?.last_model_update ?? null)}
            icon={<RefreshCw className="h-4 w-4" />}
          />
        </div>

        {/* Sub-tool cards */}
        <section>
          <h2 className="mb-4 text-base font-semibold text-stone-800">Outils IA</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {TOOL_CARDS.map((tool) => {
              const Icon = tool.Icon;
              return (
                <button
                  key={tool.href}
                  type="button"
                  onClick={() => navigate(tool.href)}
                  className="group rounded-xl border border-stone-200 bg-white p-5 text-left transition-all hover:border-stone-300 hover:shadow-sm"
                >
                  <div className={`mb-3 inline-flex rounded-lg p-2 ${tool.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <p className="text-sm font-semibold text-stone-900 group-hover:text-stone-700">
                    {tool.title}
                  </p>
                  <p className="mt-1 text-xs text-stone-500">{tool.description}</p>
                </button>
              );
            })}
          </div>
        </section>

        {/* Algorithmes section (real data from model registry) */}
        <section>
          <h2 className="mb-4 text-base font-semibold text-stone-800">Algorithmes</h2>
          {modelsLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-stone-200 border-t-stone-600" />
            </div>
          ) : models.length === 0 ? (
            <EmptyState
              title="Aucun algorithme configuré"
              description="Les algorithmes seront disponibles après le premier calcul de données."
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {models.map((model) => (
                <ModelCard key={model.model_name} model={model} />
              ))}
            </div>
          )}
        </section>

        {/* Recent insights */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-semibold text-stone-800">Informations récentes</h2>
            <button
              type="button"
              onClick={() => navigate("/admin/ai/insights")}
              className="text-xs text-stone-500 hover:text-stone-700"
            >
              Voir tout →
            </button>
          </div>

          {insightsLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-6 w-6 animate-spin rounded-full border-4 border-stone-200 border-t-stone-600" />
            </div>
          ) : insights.length === 0 ? (
            <EmptyState
              title="Aucune information disponible"
              description="Les insights IA seront générés prochainement."
            />
          ) : (
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {insights.map((insight) => (
                <InsightCard key={insight.id} insight={insight} />
              ))}
            </div>
          )}
        </section>
      </div>
    </AdminLayout>
  );
}
